import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TFunction } from 'i18next';
import FormPasswordField from '../../molecules/form-elements/FormPasswordField';
import StyledForm from '../../forms/shared/StyledForm';
import useLocalization from '../../../hooks/useLocalization';

export const getPasswordSchema = (t: TFunction) => {
  return z
    .object({
      password: z.string().min(8, t('register.validation.passwordTooShort')),
      confirmPassword: z.string().min(8, t('register.validation.passwordTooShort')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ['confirmPassword'],
      message: t('register.validation.passwordsDoNotMatch'),
    });
};

export type PasswordFormData = z.infer<ReturnType<typeof getPasswordSchema>>;

type BasePasswordFormProps = {
  onClose: () => void;
  onSubmit: (data: PasswordFormData) => void;
  title: string;
  isSubmitting: boolean;
  submitTitle?: string;
};

const BasePasswordForm = ({ onClose, onSubmit, title, isSubmitting, submitTitle }: BasePasswordFormProps) => {
  const { t } = useLocalization();
  const passwordSchema = getPasswordSchema(t);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleFormSubmit = (data: PasswordFormData) => {
    onSubmit(data);
    handleClose();
  };

  return (
    <StyledForm
      onSubmit={handleSubmit(handleFormSubmit)}
      onClose={handleClose}
      submitTitle={submitTitle || t('accept')}
      title={title}
      isSubmitting={isSubmitting}
    >
      <FormPasswordField
        name='password'
        control={control}
        label={t('register.password')}
        error={!!errors.password}
        helperText={errors.password?.message}
        disabled={isSubmitting}
      />
      <FormPasswordField
        name='confirmPassword'
        control={control}
        label={t('register.confirmPassword')}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword?.message}
        disabled={isSubmitting}
      />
    </StyledForm>
  );
};

export default BasePasswordForm;
